import { Form } from "antd/es";
import * as React from "react";
import KuikaInput from "../../ui-kit/kuika-input/kuika-input";
import { KuikaEditorTitle } from "../../ui-kit/kuika-page-header/kuika-editor-title";
import {
  EMAIL_TEMPLATE_NAME_LENGTH_MESSAGE,
  EMAIL_TEMPLATE_NAME_MAX_LENGTH,
  EMAIL_TEMPLATE_NAME_MESSAGE,
  EMAIL_TEMPLATE_NAME_PATTERN
} from "./email-template-name-modal";

/**
 * The editor bar's name doubles as the rename control, the way it always has — this is the
 * bar's `renderTitle` node. The identity beside it (the template switcher chip) is handed
 * in by the header, so this component owns only the field and its rules.
 *
 * It must render inside the editor's antd `Form`: the field is `name`, which
 * `setFieldsAndUpdate` seeds and `update()` reads back. Before the refactor the two modes
 * were both mounted and toggled with `display:none`; they are conditional now, which is
 * why the commit path validates explicitly (see `handleSubmit`).
 */

export interface INotificationTemplateTitleProps {
  /** What the template IS while it is not being renamed: the switcher chip. */
  identity: React.ReactNode;
  templateName: string;
  isEditing: boolean;
  /** System templates (reset-password and friends) cannot be renamed — no pencil at all. */
  canRename: boolean;
  onStartEdit: () => void;
  /** Called with the validated name; the host commits it to its own state. */
  onSubmit: (name: string) => void;
  onAbort: () => void;
  /** The field's uniqueness rule, owned by the host so create and rename share one. */
  nameValidator: (rule: unknown, value: string) => Promise<void>;
  /** Runs the field's rules and resolves the accepted name, or `undefined` if invalid. */
  validateName: () => Promise<string | undefined>;
}

export class NotificationTemplateTitle extends React.PureComponent<INotificationTemplateTitleProps> {
  /**
   * Validation is awaited rather than read off the last result. `existingNameValidator` is
   * async, and the field is freshly mounted each time the pencil is clicked, so
   * `getFieldError` would be empty on the first Enter and let a duplicate through. An
   * invalid name still fails silently — the field's own message is the feedback, exactly
   * as before.
   */
  handleSubmit = async (): Promise<void> => {
    const name: string | undefined = await this.props.validateName();
    if (name === undefined) return;
    this.props.onSubmit(name);
  };

  handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>): void => {
    if (e.key !== "Enter") {
      return;
    }
    // Stops the antd Form from submitting and keeps the designer's global key handlers
    // from seeing this Enter.
    e.stopPropagation();
    e.preventDefault();
    void this.handleSubmit();
  };

  render() {
    return (
      <KuikaEditorTitle
        className="kuika-email-builder__title"
        entityLabel="template"
        identity={this.props.identity}
        isRenaming={this.props.isEditing}
        // System templates (reset-password and friends) cannot be renamed — no pencil at all.
        canRename={this.props.canRename}
        // An as-yet-unnamed template has nothing to abort back to, so no cancel.
        canAbortRename={!!this.props.templateName}
        onStartRename={this.props.onStartEdit}
        onConfirmRename={this.handleSubmit}
        onAbortRename={this.props.onAbort}
        editTestId="emailBuilderNameInputButton"
        confirmTestId="confirmButton"
        abortTestId="cancelButton"
        renameField={
          <Form.Item
            name="name"
            className="kuika-email-builder__title-field"
            rules={[
              { required: true, message: "Please enter a name" },
              { pattern: EMAIL_TEMPLATE_NAME_PATTERN, message: EMAIL_TEMPLATE_NAME_MESSAGE },
              { max: EMAIL_TEMPLATE_NAME_MAX_LENGTH, message: EMAIL_TEMPLATE_NAME_LENGTH_MESSAGE },
              { validator: this.props.nameValidator }
            ]}
          >
            <KuikaInput
              data-testid="emailBuilderEditNameInput"
              onKeyDown={this.handleKeyDown}
              placeholder="Name"
              autoFocus
            />
          </Form.Item>
        }
      />
    );
  }
}
